import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { useSEO } from "@/hooks/useSEO";

const steps = [
  { icon: "ri-login-box-line", title: "登录账号", desc: "使用桔子视频账号登录，即可开启视频分享功能。" },
  { icon: "ri-upload-cloud-2-line", title: "上传作品", desc: "选择你的视频文件，填写标题、分类与简介信息。" },
  { icon: "ri-shield-check-line", title: "内容审核", desc: "平台会对上传内容进行正版与合规审核，保障观看体验。" },
  { icon: "ri-share-forward-line", title: "发布分享", desc: "审核通过后作品即可上线，生成分享链接与好友互动。" },
];

const faqs = [
  {
    q: "分享视频需要付费吗？",
    a: "普通用户分享原创视频完全免费。若涉及版权合作与内容分成，会有专属的合作方案。",
  },
  {
    q: "支持哪些格式的视频文件？",
    a: "支持 MP4、MOV、MKV 等主流格式，建议使用 1080P 及以上分辨率以获得更好的观看效果。",
  },
  {
    q: "上传的视频多久能审核完成？",
    a: "一般情况下 24 小时内完成审核，高峰期可能稍有延迟，审核结果会通过站内消息通知。",
  },
  {
    q: "如何保证分享内容的版权合规？",
    a: "请确保你拥有所上传内容的使用权。平台会对侵权内容进行下架处理，并保留追究责任的权利。",
  },
];

export default function ShareIntro() {
  useSEO({
    title: "视频分享与上传 - 桔子视频",
    description: "桔子视频支持用户分享原创视频作品，规范的上传与审核流程，让优质内容被更多人看见。",
    keywords: "视频分享,上传视频,桔子视频,创作者,原创视频",
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <Layout>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-upload-cloud-2-line" />
                视频分享
              </span>
              <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-4xl font-heading">
                分享你的影像，让好内容被更多人看见
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                桔子视频不仅是观看影视作品的平台，也是内容创作者分享作品的舞台。你可以上传原创短片、影视解说、生活记录等内容，与志同道合的观众一起交流、发现与成长。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-upload-2-line text-base" />
                  立即上传
                </button>
                <Link
                  to="/category/movie"
                  className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  先去逛逛
                </Link>
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                示范说明：本页为功能演示，上传入口仅展示交互流程，暂不提供真实上传接口。
              </p>
            </div>
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20orange%20creative%20video%20editing%20workspace%20with%20a%20laptop%20showing%20a%20timeline%20and%20camera%20gear%2C%20soft%20natural%20light%2C%20cozy%20studio%20setting%2C%20inspiring%20creator%20atmosphere%2C%20highly%20detailed%20photographic%20style&width=1200&height=675&seq=vp-share-01&orientation=landscape"
                alt="视频创作工作台"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">分享流程</h2>
        <p className="mt-1 text-sm text-foreground-500">四步轻松发布，让你的作品快速上线</p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-700">
                  <i className={step.icon} />
                </span>
                <span className="text-2xl font-bold text-background-300 font-heading">
                  0{index + 1}
                </span>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 py-12 md:py-16">
        <div className="mx-auto max-w-[900px] px-4 md:px-6">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">常见问题</h2>
          <p className="mt-1 text-sm text-foreground-500">关于视频分享，你可能会关心的问题</p>
          <div className="mt-6 space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={faq.q}
                className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-foreground-950 md:text-base">
                    {faq.q}
                  </span>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-500">
                    <i
                      className={
                        openFaq === index ? "ri-subtract-line text-lg" : "ri-add-line text-lg"
                      }
                    />
                  </span>
                </button>
                {openFaq === index && (
                  <p className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col items-center gap-4 rounded-lg bg-primary-500 px-6 py-10 text-center md:py-12">
          <h2 className="text-xl font-bold text-background-50 md:text-2xl font-heading">
            准备好分享你的第一支视频了吗？
          </h2>
          <p className="max-w-xl text-sm text-background-100">
            加入桔子视频创作者社区，让每一份热爱都被更多人看见。
          </p>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-upload-2-line text-base" />
            开始上传
          </button>
        </div>
      </section>

      {uploadOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-background-50 p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                <i className="ri-upload-cloud-2-line" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-foreground-950">上传功能演示</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  这是上传入口的交互演示。正式版本中，你可以在此选择视频文件、填写标题与简介，并提交审核后发布。当前演示环境暂不支持真实上传。
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
              >
                我知道了
              </button>
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                返回浏览
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}